import React from 'react';
import { Check } from 'lucide-react';
import Loader from './Loader';
import { orderMenusForPicker, toggleMenuWithFamily, withoutCareHomeOnlyMenus } from '../utils/menuTree';
import { useAuth } from '../context/AuthContext';

/**
 * Where a staff member can sign in — the two INDEPENDENT axes.
 *
 *   Carer toggle  → the mobile app. This replaced the old
 *                   "Management / Care staff" dropdown: the API derives the
 *                   right carer role id from it, including picking the
 *                   domiciliary carer role rather than the care-home one.
 *   menu_ids      → the web dashboard.
 *
 * Either, or both, but never neither — somebody with neither has nowhere to
 * sign in, which the caller enforces before submitting.
 *
 * Shared by the Add & Edit Staff form and the Recruitment transfer, so "is this
 * person a carer?" is asked the same way and answered into the same fields
 * whichever door they came through. It is also the ONLY place recruitment asks
 * it — the Add Candidate form deliberately does not, because an applicant's
 * role can change between applying and being offered a job.
 *
 * @param {boolean}  isCarer
 * @param {boolean}  appAccess       mobile sign-in without being a carer
 * @param {boolean}  medicationAccess carer may record medication doses in the app
 * @param {number[]} menuIds
 * @param {Array}    availableMenus
 * @param {boolean}  loadingMenus
 * @param {Function} onChange        called with a partial patch
 * @param {boolean}  canMakeManager  the signed-in user is the account owner —
 *                                   only they see the Manager switch
 * @param {boolean}  isManager       this person is (to be) a manager
 * @param {Array}    sites           multi-site groups: every site, { id, name };
 *                                   two or more shows the site ticks for a manager
 * @param {number[]} siteIds         the sites ticked for this manager
 */
const StaffAccessPicker = ({
  isCarer, appAccess, medicationAccess = false, menuIds = [], availableMenus = [], loadingMenus = false, onChange,
  canMakeManager = false, isManager = false, sites = [], siteIds = [],
}) => {
  // A domiciliary organisation is never offered a care-home-only module
  // (the Care Planner) — it would grant a page they cannot open.
  const { user } = useAuth();
  const menus = withoutCareHomeOnlyMenus(availableMenus, user?.organization?.organization_type);

  const toggleMenu = (menu) => onChange({
    menu_ids: toggleMenuWithFamily(menuIds, menus, menu),
  });

  // Owner-only. A manager gets exactly what an admin-made manager always got —
  // the organisation's modules and the app — so there is nothing to pick.
  const managerSwitch = canMakeManager && (
    <div className={`flex items-start gap-4 p-4 rounded-xl border transition-colors ${isManager
      ? 'border-[#2563EB] bg-[#EFF6FF] dark:bg-blue-900/20'
      : 'border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f]'
    }`}
    >
      <button
        type="button"
        role="switch"
        aria-checked={isManager}
        onClick={() => onChange(isManager ? { is_manager: false } : { is_manager: true, is_carer: false })}
        className={`relative shrink-0 mt-0.5 w-11 h-6 rounded-full transition-colors ${isManager ? 'bg-[#2563EB]' : 'bg-[#CBD5E1] dark:bg-gray-600'}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${isManager ? 'translate-x-5' : 'translate-x-0'}`}
        />
      </button>
      <div className="min-w-0">
        <p className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200">Management</p>
        <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1 leading-relaxed">
          {isManager
            ? 'They run the organisation day to day: every module you hold, adding and managing staff and carers, and the mobile app (view-only).'
            : 'Turn on to make this person a manager. Only you, as the account owner, can add or remove managers.'}
        </p>
      </div>
    </div>
  );

  // Multi-site groups: which sites this manager runs. At least one stays
  // ticked — a manager with no site would have nowhere to work.
  const ticked = siteIds.map(Number);
  const toggleSite = (id) => {
    const next = ticked.includes(id) ? ticked.filter((s) => s !== id) : [...ticked, id];
    if (next.length) onChange({ site_ids: next });
  };
  const siteTicks = isManager && sites.length > 1 && (
    <div className="sm:ml-8 space-y-2.5">
      <p className="text-[13px] text-[#64748B] dark:text-gray-400">
        <span className="font-semibold text-[#1E293B] dark:text-gray-200">Sites they manage.</span>{' '}
        They can switch between these from the top bar and see only these.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {sites.map((site) => {
          const on = ticked.includes(Number(site.id));
          return (
            <button
              key={site.id}
              type="button"
              onClick={() => toggleSite(Number(site.id))}
              className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${on
                ? 'border-[#2563EB] bg-[#EFF6FF] dark:bg-blue-900/20'
                : 'border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] hover:border-[#CBD5E1]'
              }`}
            >
              <div className={`w-5 h-5 shrink-0 rounded-[6px] border flex items-center justify-center ${on
                ? 'bg-[#2563EB] border-[#2563EB]'
                : 'border-[#CBD5E1] dark:border-gray-600 bg-white dark:bg-[#31373f]'
              }`}
              >
                {on && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
              </div>
              <span className="text-[13px] font-medium text-[#1E293B] dark:text-gray-200 truncate">{site.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  if (isManager) {
    return (
      <div className="space-y-6">
        {managerSwitch}
        {siteTicks}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {managerSwitch}

      {/* The one thing that decides mobile app access. */}
      <div className={`flex items-start gap-4 p-4 rounded-xl border transition-colors ${isCarer
        ? 'border-[#2563EB] bg-[#EFF6FF] dark:bg-blue-900/20'
        : 'border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f]'
      }`}
      >
        <button
          type="button"
          role="switch"
          aria-checked={isCarer}
          onClick={() => onChange({
            is_carer: !isCarer,
            // A carer always uses the app, so turning Carer on turns it on and
            // locks it. Turning Carer off leaves the choice where it was rather
            // than silently revoking a login they may still need.
            app_access: !isCarer ? true : appAccess,
          })}
          className={`relative shrink-0 mt-0.5 w-11 h-6 rounded-full transition-colors ${isCarer ? 'bg-[#2563EB]' : 'bg-[#CBD5E1] dark:bg-gray-600'}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${isCarer ? 'translate-x-5' : 'translate-x-0'}`}
          />
        </button>
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200">Carer</p>
          <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1 leading-relaxed">
            They deliver care and use the mobile app to clock in, follow their rota and write care
            records. Leave this off for management and office staff.
          </p>
        </div>
      </div>

      <label
        className={`flex items-start gap-3 p-4 rounded-xl border transition-colors sm:ml-8 ${isCarer
          ? 'border-[#E2E8F0] dark:border-gray-700 bg-[#F8FAFC] dark:bg-[#20242c] cursor-not-allowed'
          : appAccess
            ? 'border-[#2563EB] bg-[#EFF6FF] dark:bg-blue-900/20 cursor-pointer'
            : 'border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          className="sr-only"
          disabled={isCarer}
          checked={isCarer || appAccess}
          onChange={(e) => onChange({ app_access: e.target.checked })}
        />
        <div
          className={`w-5 h-5 shrink-0 mt-0.5 rounded-[6px] border flex items-center justify-center transition-colors ${
            isCarer || appAccess
              ? 'bg-[#2563EB] border-[#2563EB]'
              : 'border-[#CBD5E1] dark:border-gray-600 bg-white dark:bg-[#31373f]'
          }`}
        >
          {(isCarer || appAccess) && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
        </div>
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200">Mobile app access</p>
          <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1 leading-relaxed">
            {isCarer
              ? 'Always on for a carer — the app is how they work. Turn Carer off if you want to choose.'
              : 'Lets this staff member sign in to the app without being a carer. They will be view-only: they can see residents, care plans and the rota, but cannot clock in or record anything.'}
          </p>
        </div>
      </label>

      {/* Medication logging — a carer-only permission. Off by default: without
          it the carer still sees the medication chart in the app but cannot
          record a dose. Enforced by the API, not just hidden in the app. */}
      {isCarer && (
        <label
          className={`flex items-start gap-3 p-4 rounded-xl border transition-colors sm:ml-8 cursor-pointer ${medicationAccess
            ? 'border-[#2563EB] bg-[#EFF6FF] dark:bg-blue-900/20'
            : 'border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f]'
          }`}
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={medicationAccess}
            onChange={(e) => onChange({ medication_access: e.target.checked })}
          />
          <div
            className={`w-5 h-5 shrink-0 mt-0.5 rounded-[6px] border flex items-center justify-center transition-colors ${medicationAccess
              ? 'bg-[#2563EB] border-[#2563EB]'
              : 'border-[#CBD5E1] dark:border-gray-600 bg-white dark:bg-[#31373f]'
            }`}
          >
            {medicationAccess && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
          </div>
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200">Can log medication</p>
            <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1 leading-relaxed">
              {medicationAccess
                ? 'This carer can record medication doses (given, refused, withheld…) from the mobile app.'
                : 'Off: this carer can see residents’ medication in the app but cannot record a dose. Tick it for carers trained to administer medication.'}
            </p>
          </div>
        </label>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13px] text-[#64748B] dark:text-gray-400">
          <span className="font-semibold text-[#1E293B] dark:text-gray-200">Dashboard access (optional).</span>{' '}
          {isCarer
            ? 'Tick any modules this carer should also be able to open on the web dashboard — leave them all clear if they only use the app.'
            : 'Select which menus this staff member can access in the portal.'}
        </p>
        <div className="flex items-center gap-3 bg-[#F8FAFC] dark:bg-[#20242c] px-3 py-1.5 rounded-lg border border-[#E2E8F0] dark:border-gray-800">
          <button
            type="button"
            onClick={() => onChange({ menu_ids: menus.map((menu) => menu.id) })}
            className="text-[13px] text-[#2563EB] dark:text-blue-400 hover:text-blue-700 font-semibold"
          >
            Select All
          </button>
          <div className="w-px h-3.5 bg-[#CBD5E1] dark:bg-gray-700" />
          <button
            type="button"
            onClick={() => onChange({ menu_ids: [] })}
            className="text-[13px] text-[#64748B] dark:text-gray-400 hover:text-[#0F172A] font-semibold"
          >
            Clear All
          </button>
        </div>
      </div>

      {loadingMenus ? (
        <div className="flex items-center justify-center py-12">
          <Loader text="Loading permissions..." />
        </div>
      ) : menus.length === 0 ? (
        <div className="bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-900/50 rounded-xl p-4 text-orange-800 dark:text-orange-400 text-[13px] font-medium">
          No menus available. Please contact administrator.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
          {orderMenusForPicker(menus).map((menu) => {
            const isSelected = menuIds.includes(menu.id);
            // A sub-menu sits right after its parent and is marked as one, so
            // the list reads the way the sidebar does.
            const isSub = menu.depth === 1;
            return (
              <button
                key={menu.id}
                type="button"
                onClick={() => toggleMenu(menu)}
                className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all text-left ${isSub ? 'sm:ml-5' : ''} ${isSelected
                  ? 'border-[#2563EB] bg-[#EFF6FF] dark:bg-blue-900/20'
                  : 'border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] hover:border-[#CBD5E1] hover:bg-[#F8FAFC] dark:hover:bg-[#383e47]'
                }`}
              >
                <div
                  className={`w-5 h-5 shrink-0 rounded-[6px] border flex items-center justify-center transition-colors ${isSelected
                    ? 'bg-[#2563EB] border-[#2563EB]'
                    : 'border-[#CBD5E1] dark:border-gray-600 bg-white dark:bg-[#31373f]'
                  }`}
                >
                  {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                </div>
                <div className="min-w-0">
                  <p className={`font-medium text-[13px] truncate ${isSelected ? 'text-[#1E293B] dark:text-gray-200' : 'text-[#475569] dark:text-gray-400'}`}>
                    {isSub && <span className="text-[#94A3B8] dark:text-gray-500 mr-1">↳</span>}
                    {menu.name}
                  </p>
                  <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-0.5 truncate">
                    {isSub ? `Sub-menu · ${menu.code}` : menu.code}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StaffAccessPicker;
